import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { DomainKeyValue, KeyValueTypes, SortDirection, TaskInterface, TaskSdkService } from '@vendasta/task';
import {
  GroupedMetadata,
  KeyValues,
  SearchTaskRequest,
  SearchTaskResponse,
  SortInterface,
  TaskManagerApiService,
} from '@vendasta/task-manager';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { SendMessageDialogService } from '../send-message-dialog/send-message-dialog.service';
import {
  ACCESS,
  ACCOUNT_GROUP,
  DEFAULT_PAGE_SIZE,
  FULFILLING_PARTNER_ID,
  PARTNER,
  TaskStatus,
  TaskType,
} from '../shared/constants';
import { OrganizationType } from '../shared/interfaces';
import { validateSorts } from '../shared/utils';

@Injectable()
export class ProjectListCardService {
  private loadingProjects$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly loadingProjects$: Observable<boolean> = this.loadingProjects$$.asObservable();

  constructor(
    private taskManagerService: TaskManagerApiService,
    private accountGroupService: AccountGroupService,
    private router: Router,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly sendMsgDialogService: SendMessageDialogService,
  ) {}

  getAccountGroupInfo(accountGroupId: string): Observable<AccountGroup> {
    return this.accountGroupService.get(accountGroupId, new ProjectionFilter({ napData: true })).pipe(
      catchError(() => {
        return of({} as AccountGroup);
      }),
    );
  }

  getAllProjects(
    partnerId: string,
    accountGroupId: string,
    pageSize?: number,
    vendorTasks?: boolean,
    taskStatus?: TaskStatus[],
  ): Observable<TaskInterface[]> {
    return this.searchTasks(
      partnerId,
      accountGroupId,
      taskStatus,
      [TaskType.PROJECT],
      false,
      pageSize ?? DEFAULT_PAGE_SIZE,
      [{ field: 'due_date', direction: SortDirection.SORT_DIRECTION_ASCENDING }],
      vendorTasks,
      vendorTasks,
    ).pipe(
      map((response) => response?.tasks ?? []),
      catchError((err) => {
        this.loadingProjects$$.next(false);
        throw err;
      }),
      tap(() => this.loadingProjects$$.next(false)),
    );
  }

  private searchTasks(
    partnerId: string,
    accountGroupId?: string,
    statuses?: string[],
    types?: string[],
    deletedOnly?: boolean,
    pageSize: number = DEFAULT_PAGE_SIZE,
    sorts?: SortInterface[],
    includeVendorTasks?: boolean,
    vendorSpecificTasks?: boolean,
  ): Observable<SearchTaskResponse> {
    const extendedSorts: SortInterface[] = [{ field: 'due_date', direction: SortDirection.SORT_DIRECTION_ASCENDING }];
    extendedSorts.push({ field: 'title.raw', direction: SortDirection.SORT_DIRECTION_ASCENDING });
    if (sorts && sorts.length > 0) {
      extendedSorts.unshift(...sorts);
    }
    const validSorts = validateSorts(extendedSorts);

    const request: SearchTaskRequest = new SearchTaskRequest({
      pageSize: pageSize,
      status: statuses,
      access: ACCESS,
      excludeHistory: true,
      types: types,
      sorts: validSorts,
      deletedOnly: deletedOnly,
    });
    if (accountGroupId) {
      request.namespace = TaskSdkService.buildAccountGroupNamespace(partnerId, accountGroupId);
    } else {
      request.metadata = new KeyValues(
        new DomainKeyValue().set(PARTNER, [partnerId], KeyValueTypes.STRING).toKeyValue(),
      );
    }
    if (includeVendorTasks) {
      let metaGroups: KeyValues[];
      if (vendorSpecificTasks) {
        request.namespace = null;
        metaGroups = [
          new KeyValues(
            new DomainKeyValue()
              .set(FULFILLING_PARTNER_ID, [partnerId], KeyValueTypes.STRING)
              .set(ACCOUNT_GROUP, [accountGroupId], KeyValueTypes.STRING)
              .toKeyValue(),
          ),
        ];
      } else {
        metaGroups = [
          // When searching on FulfillingPartnerId the partner needs to be included since not all tasks have FulfillingPartnerId set
          new KeyValues(new DomainKeyValue().set(PARTNER, [partnerId], KeyValueTypes.STRING).toKeyValue()),
          new KeyValues(
            new DomainKeyValue().set(FULFILLING_PARTNER_ID, [partnerId], KeyValueTypes.STRING).toKeyValue(),
          ),
        ];
      }
      request.metadata = null;
      request.metadataGroups = new GroupedMetadata({
        metadataGroup: metaGroups,
      });
    }

    return this.taskManagerService.search(request);
  }

  goToProjectDetail(project: TaskInterface) {
    const taskId = project?.identity?.taskId;
    const agid = project?.metadata?.keyValues?.find((data) => data.key === 'account-group')?.values[0]?.stringValue;
    const url = this.buildProjectDetailUrl(agid, taskId);
    this.router.navigateByUrl(url);
  }

  private buildProjectDetailUrl(agid: string, projectId: string): string {
    //Partner Center usecase
    return 'task-manager/reporting/accounts/' + agid + '/projects/' + projectId;
  }

  goToAccountProjectList(agid: string, companyName: string): void {
    const url = 'task-manager/reporting';
    const queryParams = { 'project-report': JSON.stringify({ account: { accountGroupId: agid, name: companyName } }) };
    this.router.navigate([url], {
      queryParams: queryParams,
    });
  }

  redirectToConversation(
    partnerId: string,
    recipientOrganizationId: string,
    organizationType: OrganizationType,
    prefilledMessage?: string,
  ): void {
    this.sendMsgDialogService.redirectToConversation(
      partnerId,
      recipientOrganizationId,
      organizationType,
      prefilledMessage,
    );
  }
}
